<template>
  <!-- For now, we will only focus on the transaction original values, not the assignments, if we need to add that logic later, we will. -->
  <BModal
    v-model="modal"
    title="Edit Transaction"
    ref="modalRef"
    ok-title="Save"
    ok-variant="success"
    @show="initTrsxnState"
    @ok="handleTransactionSubmission"
  >
    <div class="text-center">
      <h6>Transaction Details</h6>
      <BFormGroup
        id="input-group-institution"
        label="Institution:"
        label-for="institution-options"
        class="floating-group"
      >
        <BFormSelect
          id="institution-options"
          v-model="institution"
          :class="{ 'is-invalid': instError !== null }"
          :options="institutionOpts"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-amount"
        label="Amount:"
        label-for="amount-input"
        class="floating-group"
      >
        <BFormInput
          id="amount-input"
          v-model="amount"
          type="number"
          step="0.01"
          :class="{ 'is-invalid': amountError !== null }"
          placeholder="0.00"
          style="font-size: 0.78rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-date"
        label="Date:"
        label-for="date-input"
        class="floating-group"
      >
        <BFormInput
          id="date-input"
          v-model="date"
          type="date"
          :class="{ 'is-invalid': dateError !== null }"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-description"
        label="Description:"
        label-for="description-input"
        class="floating-group"
      >
        <BFormTextarea
          id="description-input"
          v-model="description"
          :class="{ 'is-invalid': descriptionError !== null }"
          placeholder="Enter something..."
          rows="2"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
      <BFormGroup
        id="input-group-note"
        label="Notes:"
        label-for="note-input"
        class="floating-group"
      >
        <BFormTextarea
          id="note-input"
          v-model="note"
          :class="{ 'is-invalid': noteError !== null }"
          placeholder="Enter something..."
          rows="2"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
      <h6 class="mt-3">Snapshot Category Allocation(s)</h6>
      <div
        v-if="snapshotCategories.length === 0"
        class="alert alert-secondary py-2 mb-2"
      >
        <small>No snapshot category allocations for this transaction.</small>
      </div>
      <div
        v-for="(allocation, index) in snapshotCategories"
        :key="`${allocation.spending_category_uid}-${index}`"
        class="border rounded px-2 py-2 mb-2 text-start"
      >
        <div class="small">
          <strong>Category UID:</strong> {{ allocation.spending_category_uid }}
        </div>
        <div class="small">
          <strong>Amount:</strong>
          {{ Number(allocation.fixed_amount).toFixed(2) }}
        </div>
      </div>
      <h6 class="mt-3">Weekly Outlook Assignation(s)</h6>
      <div
        v-if="loadingWeeklyOutlookAssignments"
        class="alert alert-info py-2 mb-2"
      >
        <small><strong>Loading assignments...</strong></small>
      </div>
      <div class="d-flex align-items-center">
        <BFormGroup
          id="input-group-weekly-outputs"
          label="Weekly Outlook(s):"
          label-for="weekly-outputs"
          class="floating-group flex-grow-1 me-2"
        >
          <BFormSelect
            id="weekly-outputs"
            :modelValue="selectedWeek"
            :options="weeksAsOpts"
            size="sm"
            style="font-size: 0.85rem"
            :disabled="loadingWeeklyOutlookAssignments"
            :class="{ 'is-invalid': selectedWeeksError !== null }"
            @update:modelValue="handleWeekSelection"
          />
        </BFormGroup>
        <BButton
          variant="outline-primary"
          class="ms-2 ms-auto mt-2"
          size="sm"
          :disabled="loadingWeeklyOutlookAssignments"
          @click="handleWeekSelection(thisMonday)"
        >
          This Week
        </BButton>
      </div>
      <p v-if="selectedWeeksError" class="small mb-0 text-danger">
        {{ selectedWeeksError }}
      </p>
      <BFormGroup
        v-for="(weekObj, week) in selectedWeeks"
        :key="week"
        :id="`input-group-weekly-output-${week}`"
        :label="`${week}'s Amount:`"
        :label-for="`weekly-output-${week}`"
        class="floating-group px-3"
      >
        <div class="d-flex align-items-center">
          <BFormInput
            v-model.number="weekObj.amount"
            type="number"
            step="0.01"
            size="sm"
            placeholder="0.00"
            style="font-size: 0.78rem"
          />
          <IconTrash
            size="16"
            class="text-danger ms-2"
            style="cursor: pointer"
            @click="handleWeekDeselection(week)"
          />
        </div>
      </BFormGroup>
    </div>
  </BModal>
</template>
<script>
import {
  BModal,
  BFormSelect,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
} from "bootstrap-vue-next";
import {
  bankingInstitutions as bankingInstitutionsNs,
  weeklyOutlooks as weeklyOutlooksNs,
} from "@/store/namespaces";
import { mapGetters } from "vuex";
import { IconTrash } from "@tabler/icons-vue";

const weeklyOutputObj = {
  week: null,
  amount: null,
};

export default {
  name: "EditTransactionModal",
  components: {
    BModal,
    BFormSelect,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    IconTrash,
  },
  props: {
    txn: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      modal: false,
      amount: null,
      amountError: null, // Is required and can't be 0
      date: null,
      dateError: null, // Is required
      description: null,
      descriptionError: null, // Is required
      note: null,
      noteError: null, // Is optional
      institution: null,
      instError: null, // Is required
      loadingWeeklyOutlookAssignments: false,
      weeklyOutlookAssignmentsError: null,
      selectedWeek: null,
      selectedWeeks: {},
      thisMonday: null,
    };
  },
  computed: {
    ...mapGetters({
      vuexWeeks: `${weeklyOutlooksNs}/getWeeklyOutlooks`,
    }),
    bankingInstitutions() {
      return this.$store.getters[
        `${bankingInstitutionsNs}/getBankingInstitutions`
      ];
    },
    snapshotCategories() {
      if (!Array.isArray(this.txn?.snapshot_categories)) return [];

      return this.txn.snapshot_categories.filter(
        (allocation) => allocation && allocation.spending_category_uid
      );
    },
    institutionOpts() {
      const opts = [{ value: null, text: "— Select —" }];

      if (!this.bankingInstitutions) return opts;

      const institutionOpts = this.bankingInstitutions.map((inst) => {
        const uidLast6 = inst.uid.slice(-6);
        return {
          value: inst,
          text: `${inst.name} - ${inst.acct_last_4} (${uidLast6})`,
        };
      });

      return opts.concat(institutionOpts);
    },
    weeksAsOpts() {
      if (!this.vuexWeeks) return [];

      return [
        { value: null, text: "— Select a week —", disabled: true },
        ...this.vuexWeeks.map((week) => ({
          value: week.weekly_outlook,
          text:
            week.weekly_outlook === this.thisMonday
              ? `${week.weekly_outlook} (This Week)`
              : week.weekly_outlook,
        })),
      ];
    },
    selectedWeeksError() {
      if (Object.keys(this.selectedWeeks).length === 0) return null;

      // Ensure no week has an amount of 0
      const weeks = this.selectedWeeks;
      const zeroAmntWeek = Object.values(weeks).find((week) => !week.amount);
      const zeroAmntErr = "Assigned weeks cannot have an amount of 0";
      if (zeroAmntWeek) return zeroAmntErr;

      // Ensure total assigned to weeks does not exceed transaction amount
      const weeksAmnts = Object.values(weeks).map((week) => week.amount);
      const amntsSum = weeksAmnts.reduce((sum, amt) => sum + (amt || 0), 0);
      const exceedsErr = `Total assigned to weeks cannot exceed transaction amount`;
      if (Math.abs(amntsSum) > Math.abs(this.txn.amount)) return exceedsErr;

      return null;
    },
  },
  mounted() {
    // Calculate this week's monday in format YYYY-MM-DD
    const monday = new Date();
    const dayIdx = monday.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    monday.setDate(monday.getDate() - (dayIdx === 0 ? 6 : dayIdx - 1)); // Adjust to get to Monday (if Sunday, go back 6 days)
    const year = monday.getFullYear();
    const month = String(monday.getMonth() + 1).padStart(2, "0");
    const date = String(monday.getDate()).padStart(2, "0");
    const formattedMonday = `${year}-${month}-${date}`;
    this.thisMonday = formattedMonday;
  },
  methods: {
    handleWeekSelection(week) {
      this.selectedWeek = null; // Reset the select input
      if (!week) return;

      if (!this.selectedWeeks[week]) {
        this.selectedWeeks[week] = { ...weeklyOutputObj, week };

        if (Object.keys(this.selectedWeeks).length === 1) {
          this.selectedWeeks[week].amount = this.txn.amount;
        }
      }
    },
    handleWeekDeselection(week) {
      delete this.selectedWeeks[week];
    },
    async fetchWeeklyOutlookAssignments() {
      this.weeklyOutlookAssignmentsError = null;

      if (!this.txn?.acct_uid || !this.txn?.transaction_uid) {
        this.weeklyOutlookAssignmentsError =
          "Missing account or transaction context for weekly outlook assignments.";
        return [];
      }

      try {
        this.loadingWeeklyOutlookAssignments = true;
        const response = await this.$api.get("/weekly-outlooks/assignments", {
          params: {
            filter: [
              `acctUid:eq:${this.txn.acct_uid}`,
              `origTrsxnUid:eq:${this.txn.transaction_uid}`,
            ],
          },
        });

        return (response?.data || []).map((item) => item.item);
      } catch (error) {
        // No assignments for this transaction is expected for many cases.
        if (error?.response?.status === 404) {
          return [];
        }

        console.error("Error fetching weekly outlook assignments:", error);
        this.weeklyOutlookAssignmentsError =
          "Could not load weekly outlook assignments.";
        return [];
      } finally {
        this.loadingWeeklyOutlookAssignments = false;
      }
    },
    validateForm() {
      let isValid = true;

      if (this.amount === null || this.amount === 0) {
        this.amountError = "Amount is required and cannot be 0.";
        isValid = false;
      } else {
        this.amountError = null;
      }

      if (!this.date) {
        this.dateError = "Date is required.";
        isValid = false;
      } else {
        this.dateError = null;
      }

      if (!this.description) {
        this.descriptionError = "Description is required.";
        isValid = false;
      } else {
        this.descriptionError = null;
      }

      if (!this.institution) {
        this.instError = "Institution selection is required.";
        isValid = false;
      } else {
        this.instError = null;
      }

      return isValid;
    },
    async handleTransactionSubmission(bvEvt) {
      try {
        bvEvt.preventDefault(); // Prevent the modal from closing immediately
        const formIsValid = this.validateForm();
        if (!formIsValid) return;

        if (this.selectedWeeksError) {
          this.$notify.notifyError(this.selectedWeeksError);
          return;
        }

        if (!this.txn?.acct_uid) {
          this.$notify.notifyError(
            "Account context is missing. Please select an account and try again."
          );
          return;
        }

        const weeklyOutlookAssignments = Object.values(this.selectedWeeks).map(
          (weekObj) => ({
            acct_uid: this.txn.acct_uid,
            weekly_outlook: weekObj.week,
            amount: Number(weekObj.amount),
          })
        );

        await this.$api.patch(
          `/banking-institutions/transactions/${this.txn.transaction_uid}`,
          {
            acct_uid: this.txn.acct_uid,
            amount: Number(this.amount).toFixed(2), // Ensure amount is sent as a string with 2 decimal places
            date: this.date,
            description: this.description,
            weekly_outlook_assignments: weeklyOutlookAssignments,
            ...(this.note && this.note.trim() !== ""
              ? { note: this.note }
              : {}),
          }
        );

        this.$refs.modalRef.hide();
        this.$notify.notifySuccess("Transaction saved successfully.");
        this.resetModalState();
      } catch (error) {
        console.error("Error saving transaction:", error);
        this.$notify.notifyError(
          "An error occurred while saving the transaction. Please try again."
        );
      }
    },
    async initTrsxnState() {
      if (!this.txn) {
        console.error(
          "Transaction data is required to initialize the edit modal."
        );
        return;
      }

      this.amount = this.txn.amount;
      this.date = this.txn.date;
      this.description = this.txn.description;
      this.note = this.txn.note || null;

      const insts = [...this.institutionOpts]; // Get the institution options
      if (insts[0] && !insts[0].value) insts.shift(); // Remove the placeholder option if it exists

      const inst = insts?.find(
        (inst) => inst.value.uid === this.txn.institution_uid
      );

      this.institution = inst ? inst.value : null;

      const weeklyOutlookAssignments =
        await this.fetchWeeklyOutlookAssignments();

      this.selectedWeeks = weeklyOutlookAssignments.reduce(
        (selectedWeeks, assignment) => {
          selectedWeeks[assignment.weekly_outlook] = {
            ...weeklyOutputObj,
            week: assignment.weekly_outlook,
            amount: assignment.amount,
          };
          return selectedWeeks;
        },
        {}
      );
    },
    resetModalState() {
      this.amount = null;
      this.date = null;
      this.description = null;
      this.institution = null;
      this.amountError = null;
      this.dateError = null;
      this.descriptionError = null;
      this.instError = null;
      this.note = null;
      this.noteError = null;
      this.loadingWeeklyOutlookAssignments = false;
      this.weeklyOutlookAssignmentsError = null;
      this.selectedWeek = null;
      this.selectedWeeks = {};
      this.selectedWeeksError = null;
    },
  },
};
</script>
